/**
 * WU-07 配線: capability bootstrap（GET /admin/me/bootstrap）と
 * WU-04 の deploy 単位 enabledScreens を合成した「実効画面セット」を返す hook。
 *
 * fail-closed 方針:
 * - bootstrap 取得完了までは status: 'loading'（全画面露出のフラッシュも
 *   誤 redirect も起こさない）
 * - 取得失敗時は capabilityScreens(null) = 「見る」系のみ
 * - preview auth / legacy API_TOKEN は従来画面を維持するが、会議画面は除外する
 */
import { useEffect, useState } from 'react'

import { useAppAuth } from './auth'
import { getApiClient } from './api'
import {
  capabilityScreens,
  combineWithEnabledScreens,
  type MeBootstrapResponse,
} from './capability-config'
import { enabledScreens } from './runtime-config'
import type { EnabledScreens } from './nav-config'

export type ScreenSetState =
  | { status: 'loading' }
  | { status: 'ready'; screens: EnabledScreens }

export function useScreenSet(): ScreenSetState {
  const { isLoaded, isSignedIn, isPreviewMode } = useAppAuth()
  const [bootstrap, setBootstrap] = useState<
    { status: 'pending' } | { status: 'settled'; value: MeBootstrapResponse | null }
  >({ status: 'pending' })

  const needsBootstrap = isLoaded && isSignedIn && !isPreviewMode

  useEffect(() => {
    if (!needsBootstrap) return
    let cancelled = false
    getApiClient()
      .getMeBootstrap()
      .then((value) => {
        if (!cancelled) setBootstrap({ status: 'settled', value })
      })
      .catch(() => {
        // 取得失敗 = fail-closed fallback（capabilityScreens(null)）
        if (!cancelled) setBootstrap({ status: 'settled', value: null })
      })
    return () => {
      cancelled = true
    }
  }, [needsBootstrap])

  const deployScreens = enabledScreens()

  // preview auth / 未サインイン（login へ redirect される）は会議画面だけ除外
  if (!needsBootstrap) {
    return {
      status: 'ready',
      screens: combineWithEnabledScreens(
        capabilityScreens(null, { legacyOrPreviewAuth: true }),
        deployScreens,
      ),
    }
  }
  if (bootstrap.status === 'pending') {
    return { status: 'loading' }
  }
  return {
    status: 'ready',
    screens: combineWithEnabledScreens(
      capabilityScreens(bootstrap.value),
      deployScreens,
    ),
  }
}
